import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useContractorAuth } from "../context/ContractorAuthContext";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff, ArrowLeft, Camera } from "lucide-react";

export default function ContractorLogin() {
  const { login, register } = useContractorAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/contractor";
  const [mode, setMode] = useState(params.get("register") === "1" ? "register" : "login"); // login | register
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: params.get("email") || "", mobile: "", dbs_number: "", company: "", password: "", contractor_photo: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Please choose an image under 5MB.");
    const reader = new FileReader();
    reader.onload = () => set("contractor_photo", reader.result);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const v = params.get("verified");
    if (v === "1") toast.success("Email verified — you can now sign in and complete inductions.");
    else if (v === "invalid") toast.error("That verification link is invalid or has expired. Sign in and resend it.");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register(form);
    setBusy(false);
    if (res.ok) { toast.success(mode === "login" ? "Welcome back" : "Account created"); navigate(next); }
    else toast.error(res.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/login" className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand" data-testid="staff-login-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Staff sign in
        </Link>
        <div className="rounded-3xl border border-border bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <Logo className="mb-6" />
          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-brand">Contractor portal</div>
          <h1 className="mb-6 font-heading text-2xl font-bold tracking-tight text-foreground">
            {mode === "forgot" ? "Reset your password" : mode === "login" ? "Sign in to your account" : "Create your contractor account"}
          </h1>

          <form onSubmit={mode === "forgot" ? forgot : submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First name" testid="cr-first" value={form.first_name} onChange={(v) => set("first_name", v)} required />
                  <Field label="Surname" testid="cr-last" value={form.last_name} onChange={(v) => set("last_name", v)} required />
                </div>
                <Field label="Company" testid="cr-company" value={form.company} onChange={(v) => set("company", v)} required />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Telephone number" testid="cr-mobile" value={form.mobile} onChange={(v) => set("mobile", v)} />
                  <Field label="DBS number" testid="cr-dbs" value={form.dbs_number} onChange={(v) => set("dbs_number", v)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Upload photo</Label>
                  <div className="flex items-center gap-3">
                    {form.contractor_photo ? (
                      <img src={form.contractor_photo} alt="Preview" className="h-16 w-16 rounded-full object-cover ring-1 ring-border" data-testid="cr-photo-preview" />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Camera className="h-5 w-5" /></div>
                    )}
                    <input type="file" accept="image/*" onChange={onPhoto} data-testid="cr-photo-input"
                      className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-brand file:px-4 file:py-2 file:text-white hover:file:bg-brand-dark" />
                  </div>
                  <p className="text-xs text-muted-foreground">The photo will be used on an ID badge to wear at all times whilst on site.</p>
                </div>
              </>
            )}
            <Field label="Email address" testid="cr-email" type="email" value={form.email} onChange={(v) => set("email", v)} required />
            {mode !== "forgot" && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Password</Label>
                  {mode === "login" && (
                    <button type="button" onClick={() => setMode("forgot")} className="text-xs font-semibold text-brand hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                  )}
                </div>
                <div className="relative">
                  <Input data-testid="cr-password" type={showPw ? "text" : "password"} value={form.password}
                    onChange={(e) => set("password", e.target.value)} required minLength={6} className="pr-10" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="cr-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
            <Button type="submit" className="w-full bg-brand hover:bg-brand-dark" size="lg" disabled={busy} data-testid="cr-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "forgot" ? "Send reset link" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? (
              <>New contractor?{" "}
                <button className="font-semibold text-brand hover:underline" onClick={() => setMode("register")} data-testid="switch-register">Register here</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button className="font-semibold text-brand hover:underline" onClick={() => setMode("login")} data-testid="switch-login">Sign in</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} {...rest} />
    </div>
  );
}
